import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Nav from "./Nav";
import EditUserForm from "./EditUserForm";
import { parseJwt } from "../util/parseJwt";
import "./Homepage.css";
import SignupForm from "./SignupForm";
import UploadImageForm from "./UploadImageForm";
import Menu from "./Menu";
import HomeButton from "../assets/images/home.svg";
import Analytics2 from "./Analytics2";
import AnalyticsMarketing from "./AnalyticsMarketing";
import Dashboard from "./Dashboard";
import Quotes from "./Quotes";
import Assets from "./Assets";
import Invoices from "./Invoices";
import WorkOrders from "./WorkOrders";
import Maintenance from "./Maintenance";
import CapitalProjects from "./CapitalProjects";

// =================== Reports (define OUTSIDE component) ===================
export const REPORTS = [
  { id: 1, name: "Dashboard", group: "General" },
  { id: 2, name: "Quotes", group: "General" },
  { id: 3, name: "Work Orders", group: "Work History" },
  { id: 4, name: "Maintenance", group: "Work History" },
  { id: 5, name: "Capital Projects", group: "Work History" },
  { id: 6, name: "Assets", group: "General" },
  { id: 7, name: "Invoicing", group: "General" },
  { id: 9, name: "Analytics", group: "General" },
];

// ----------------- Debug helpers -----------------
const DEBUG = false; // flip to true locally if you want logs
const dbg = (...args) => {
  if (DEBUG) console.log("[Homepage]", ...args);
};

const Homepage = ({ isLoggedIn, handleLogout }) => {
  const [userDetails, setUserDetails] = useState({});
  const [allProperties, setAllProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [showRemoveForm, setShowRemoveForm] = useState(false);
  const [showUploadImageForm, setShowUploadImageForm] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [currentReport, setCurrentReport] = useState("Dashboard");
  const [menuOpen, setMenuOpen] = useState(false);
  const [showEditUserForm, setShowEditUserForm] = useState(false);
  const [ourCustomers, setOurCustomers] = useState([]);
  const [customerNames, setCustomerNames] = useState([]); // non-admin user’s customers
  const [selectedCustomerNames, setSelectedCustomerNames] = useState([]); // admin-impersonated user’s customers
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [nclarityBuildings, setNclarityBuildings] = useState([]);

  // tracks which pages have been opened (so we keep them mounted)
  const [mountedPages, setMountedPages] = useState(new Set());

  const dropdownRef = useRef(null);

  // ----------------- Helpers: normalization & guards -----------------
  const parseJsonArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return val
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
    }
    return [];
  };

  const normalizeNclarityBuilding = (b) => {
    if (!b) return null;
    return {
      ID: b.ID ?? b.id ?? null,
      NAME: b.NAME ?? b.name ?? null,
      ...b,
    };
  };

  const toSingleBuildopsId = (val) => {
    const out = Array.isArray(val) ? val[0] : val;
    dbg("toSingleBuildopsId in", val, "->", out);
    return out;
  };

  const resolveNClarityBuildings = async (propertyIds, token) => {
    if (!propertyIds || propertyIds.length === 0) return [];
    const results = await Promise.all(
      propertyIds.map((id) => fetchNClarityBuilding(id, token))
    );
    return results
      .map(normalizeNclarityBuilding)
      .filter((b) => b && b.ID);
  };

  // Resolve nClarity buildings for the logged-in user's BuildOps properties
  const resolveNClarityMappingOnLogin = async (decoded, token) => {
    try {
      let propertyIds = parseJsonArray(decoded?.BUILDOPS_PROPERTY_IDS);
      dbg("resolve mapping: JWT propertyIds =", propertyIds);

      // Fallback via /api/users/getproperty/:email
      if (propertyIds.length === 0 && decoded?.email) {
        const resp = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/getproperty/${decoded.email}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const u0 = (resp?.data?.user || [])[0] || {};
        propertyIds = parseJsonArray(u0?.BUILDOPS_PROPERTY_IDS);
        dbg("resolve mapping: fallback propertyIds =", propertyIds);
      }

      const buildings = await resolveNClarityBuildings(propertyIds, token);
      setNclarityBuildings(buildings);
    } catch (e) {
      console.error("resolveNClarityMappingOnLogin failed", e);
      setNclarityBuildings([]);
    }
  };

  // Log when nclarityBuildings change
  useEffect(() => {
    dbg("nclarityBuildings updated", nclarityBuildings);
  }, [nclarityBuildings]);

  // ----------------- Close dropdown on outside click -----------------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ----------------- Log mount -----------------
  useEffect(() => {
    dbg("Mounted. DEBUG logging is ON.");
  }, []);

  // ----------------- Fetch user details and properties on login -----------------
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("No token found, user is not authorized.");
      return;
    }

    try {
      const decoded = parseJwt(token);
      const currentTime = Date.now() / 1000;
      if (decoded.exp < currentTime) {
        console.error("Token expired.");
        handleLogout();
        return;
      }

      setUserDetails(decoded);
      dbg("JWT decoded", decoded);

      const isAdminFlag =
        decoded.email?.endsWith("@totalcomfortsolutions.com") ||
        decoded.email?.endsWith("@dynamiq.com");
      setIsAdmin(isAdminFlag);
      dbg("isAdmin?", isAdminFlag);

      // Always try to resolve nClarity mapping for customers
      resolveNClarityMappingOnLogin(decoded, token);

      if (!isAdminFlag) {
        // Fetch both customer names and property names by email for non-admin users
        fetchCustomersByEmail(decoded.email, token).then((customerNamesRes) => {
          if (customerNamesRes.length > 0) {
            setCustomerNames(customerNamesRes);
            fetchPropertyIdsByEmail(decoded.email, token).then(
              (propertyNames) => {
                setAllProperties(propertyNames);
                setFilteredProperties(propertyNames);
              }
            );
          } else {
            console.error("No customer names found for the user.");
          }
        });
      } else {
        fetchAdminCustomers(token);
      }
    } catch (error) {
      console.error("Failed to parse JWT:", error);
      handleLogout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------- Mount the current report once the viewer is ready -----------------
  useEffect(() => {
    if (!currentReport) return;
    // Admin must pick a customer before any page is mounted
    if (isAdmin && !selectedCustomer) return;
    setMountedPages((prev) =>
      prev.has(currentReport) ? prev : new Set([...prev, currentReport])
    );
  }, [isAdmin, selectedCustomer, currentReport]);

  // ----------------- Load our customers for admin view -----------------
  useEffect(() => {
    const fetchOurCustomers = async () => {
      try {
        const response = await fetch("/api/users/");
        const data = await response.json();
        setOurCustomers(data.users || []);
      } catch (error) {
        console.error("Error fetching our customers:", error);
      }
    };

    fetchOurCustomers();
  }, []);

  // ----------------- API helpers -----------------
  const fetchPropertyIdsByEmail = async (email, token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/users/getproperty/${email}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const user = response.data.user;

      if (user && user.length > 0) {
        const propertyIds = user[0].BUILDOPS_PROPERTY_IDS;

        if (propertyIds && propertyIds.length > 0) {
          setAllProperties(user[0].PROPERTIES);
          setFilteredProperties(user[0].PROPERTIES);
          return user[0].PROPERTIES;
        } else {
          console.error("No property names found for the user.");
          return [];
        }
      } else {
        console.error("No user data found in the response.");
        return [];
      }
    } catch (error) {
      console.error("Failed to fetch BUILDOPS_PROPERTY_IDS:", error);
      return [];
    }
  };

  const fetchCustomersByEmail = async (email, token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/users/usercustomerbyemail/${email}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const customerNamesRes = response.data.customers;

      if (!customerNamesRes || customerNamesRes.length === 0) {
        console.error("No customers found for this email.");
        return [];
      }

      return customerNamesRes.map((c) => c.CUSTOMER_NAME).flat();
    } catch (error) {
      console.error("Error fetching customers by email:", error);
      return [];
    }
  };

  const fetchAdminCustomers = async (token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/users/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const nonAdminCustomers = response.data.users.filter(
        (customer) =>
          !customer.EMAIL.endsWith("@totalcomfortsolutions.com") &&
          !customer.EMAIL.endsWith("@dynamiq.com")
      );
      setCustomers(nonAdminCustomers);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };

  const fetchNClarityBuilding = async (buildopsPropertyId, token) => {
    try {
      dbg("fetchNClarityBuilding ->", buildopsPropertyId);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/analytics/nclarity-building/${buildopsPropertyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      dbg("nClarity building API response", response?.data);
      return response.data.nclarityBuilding;
    } catch (err) {
      if (err.response?.status === 404) {
        dbg("No nClarity mapping for property", buildopsPropertyId);
        return null;
      }
      console.error("Error fetching mapped nClarity building", err);
      return null;
    }
  };

  // ----------------- Nav actions -----------------
  const handleOpenCustomReport = (report) => {
    dbg("handleOpenCustomReport", report?.name);
    setCurrentReport(report.name);
    setMountedPages((prev) => new Set([...prev, report.name]));
  };

  const handleViewAsCustomer = async (email) => {
    dbg("handleViewAsCustomer ->", email);
    const customer = ourCustomers.find((c) => c.EMAIL === email);

    if (!email || !customer) {
      console.warn("No customer selected or found.");
      setSelectedCustomer(null);
      setSelectedCustomerNames([]);
      setAllProperties([]);
      setFilteredProperties([]);
      setNclarityBuildings([]);
      return;
    }

    setSelectedCustomer(customer);

    const buildopsId = toSingleBuildopsId(customer.BUILDOPS_CUSTOMER_ID);
    const token = localStorage.getItem("token");
    dbg("view-as buildopsId", buildopsId);

    const propertyIds = parseJsonArray(customer.BUILDOPS_PROPERTY_IDS);
    dbg("view-as propertyIds", propertyIds);

    try {
      const buildings = await resolveNClarityBuildings(propertyIds, token);
      dbg("view-as nClarity buildings ->", buildings);
      setNclarityBuildings(buildings);
    } catch (err) {
      console.error("Error fetching nClarity buildings:", err);
      setNclarityBuildings([]);
    }

    if (!buildopsId) {
      console.warn("No BUILDOPS_CUSTOMER_ID found for selected customer.");
      setSelectedCustomerNames([]);
      return;
    }

    try {
      // properties for this customer
      const propertyRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/sigma/buildings/${buildopsId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const propertyNames = propertyRes.data?.property_names || [];
      dbg("view-as properties count", propertyNames?.length);

      setAllProperties(propertyNames);
      setFilteredProperties(propertyNames);

      // the impersonated user's customer names
      const customerNamesRes = await fetchCustomersByEmail(
        customer.EMAIL,
        token
      );
      dbg("view-as customerNames count", customerNamesRes?.length);
      setSelectedCustomerNames(customerNamesRes);
    } catch (err) {
      console.error(
        "Failed to fetch properties or customer names for user:",
        err
      );
      setSelectedCustomerNames([]);
    }
  };

  const toProperCase = (text) => {
    if (typeof text !== "string") return "";
    return text.replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  };

  const filteredCustomerList = ourCustomers
    .filter(
      (customer) =>
        customer.EMAIL &&
        customer.EMAIL.trim() !== "" &&
        !/(@dynamiq\.com|@dynamiq\.net|@totalcomfortsolutions\.com)$/i.test(
          customer.EMAIL
        ) &&
        customer.EMAIL.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) =>
      String(a.CUSTOMER_NAME || "").localeCompare(String(b.CUSTOMER_NAME || ""))
    );

  const handleSelectCustomer = (email) => {
    setSearchTerm(email);
    setIsDropdownOpen(false);
    handleViewAsCustomer(email);
  };

  const handleClearSearchTerm = () => {
    setSearchTerm("");
  };

  const renderContent = () => {
    // 🔒 Admins see a prompt until a user is selected
    if (isAdmin && !selectedCustomer) {
      return (
        <div className="reports-empty">
          <p>Select a user from the dropdown to load their reports.</p>
        </div>
      );
    }

    // Analytics remains special (always unmount/remount — not data-heavy)
    if (currentReport === "Analytics") {
      const showAnalytics2 = nclarityBuildings.length > 0;
      if (showAnalytics2)
        return <Analytics2 nclarityBuildings={nclarityBuildings} />;
      return <AnalyticsMarketing />;
    }

    const viewerEmail =
      isAdmin && selectedCustomer ? selectedCustomer.EMAIL : undefined;

    const pageStyle = (name) => ({
      display: currentReport === name ? "flex" : "none",
      flex: 1,
      flexDirection: "column",
      overflow: "hidden",
      minHeight: 0,
    });

    return (
      <>
        {mountedPages.has("Dashboard") && (
          <div style={pageStyle("Dashboard")}>
            <Dashboard viewerEmail={viewerEmail} />
          </div>
        )}
        {mountedPages.has("Quotes") && (
          <div style={pageStyle("Quotes")}>
            <Quotes viewerEmail={viewerEmail} />
          </div>
        )}
        {mountedPages.has("Assets") && (
          <div style={pageStyle("Assets")}>
            <Assets viewerEmail={viewerEmail} />
          </div>
        )}
        {mountedPages.has("Invoicing") && (
          <div style={pageStyle("Invoicing")}>
            <Invoices viewerEmail={viewerEmail} />
          </div>
        )}
        {mountedPages.has("Work Orders") && (
          <div style={pageStyle("Work Orders")}>
            <WorkOrders viewerEmail={viewerEmail} />
          </div>
        )}
        {mountedPages.has("Maintenance") && (
          <div style={pageStyle("Maintenance")}>
            <Maintenance viewerEmail={viewerEmail} />
          </div>
        )}
        {mountedPages.has("Capital Projects") && (
          <div style={pageStyle("Capital Projects")}>
            <CapitalProjects viewerEmail={viewerEmail} />
          </div>
        )}
      </>
    );
  };

  return (
    <div className="homepage-wrapper">
      <div className={`report-list-container`}>
        <Nav
          userDetails={userDetails}
          reports={REPORTS}
          handleOpenCustomReport={handleOpenCustomReport}
          isLoggedIn={isLoggedIn}
          handleLogout={handleLogout}
          isAdmin={isAdmin}
        />
      </div>

      <div className="homepage-content">
        <div className="header">
          <div className="left-buttons-container">
            <button
              onClick={() => setCurrentReport("Dashboard")}
              className="home-button"
            >
              <img src={HomeButton} alt="Home" />
            </button>

            {currentReport && (
              <span className="selected-report-name">{currentReport}</span>
            )}
          </div>

          {/* Admin controls */}
          {isAdmin && (
            <div className="admin-controls-container">
              <div className={`admin-controls`}>
                <button
                  className="add-user-button"
                  onClick={() => setShowSignupForm(true)}
                >
                  Add User
                </button>

                <div className="custom-dropdown" ref={dropdownRef}>
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Select User View"
                      value={searchTerm}
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="customer-search-input"
                      style={{ paddingRight: "30px" }}
                    />
                    {searchTerm && (
                      <button
                        onClick={handleClearSearchTerm}
                        style={{
                          position: "absolute",
                          right: "10px",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "16px",
                          color: "#888",
                          padding: "0",
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {isDropdownOpen && (
                    <div className="dropdown-options">
                      {filteredCustomerList.length > 0 ? (
                        filteredCustomerList.map((customer) => (
                          <div
                            key={customer.EMAIL}
                            className="dropdown-option"
                            onClick={() => handleSelectCustomer(customer.EMAIL)}
                          >
                            <strong>{customer.EMAIL}</strong>{" "}
                            {` (${customer.CUSTOMER_NAME})`}
                          </div>
                        ))
                      ) : (
                        <div className="dropdown-option" disabled>
                          No customers available
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <button
                  className="add-user-button"
                  onClick={() => setShowEditUserForm(true)}
                >
                  Edit User
                </button>
                {showEditUserForm && (
                  <EditUserForm
                    customers={customers}
                    onClose={() => setShowEditUserForm(false)}
                  />
                )}
              </div>
            </div>
          )}
          <div className="controls-container">
            <p className="customer-name" onClick={() => setMenuOpen(!menuOpen)}>
              {Array.isArray(customerNames) && customerNames.length > 0
                ? customerNames.flat().map(toProperCase).join(", ")
                : `${isAdmin ? "Admin" : "No customer on account"}`}{" "}
              -
            </p>

            <Menu
              handleLogout={handleLogout}
              username={toProperCase(userDetails.name)}
              externalToggle={menuOpen}
            />
          </div>
        </div>

        {showSignupForm && (
          <SignupForm onClose={() => setShowSignupForm(false)} />
        )}

        {renderContent()}
      </div>
    </div>
  );
};

export default Homepage;
